/**
 * Audit log vocabulary (entity types and actions written by the modules) and filter helpers (RECEK-75765).
 *
 * The entity type list is static on purpose: a SELECT DISTINCT over the multi-million-row audit log is too slow
 * for a filter. A type missing here still shows in the table under its raw name.
 */
/** Every entity name the modules write to the audit log. */
export var AUDIT_ENTITY_NAMES = [
    "title",
    "product",
    "productNumber",
    "person",
    "publisher",
    "collection",
    "organization",
    "user",
    "userOrganization",
    "onixCodetable",
    "onixCodetableItem",
    "publisherPrefixRange",
    "coEditionNumberRequest",
    "personMatchRelink",
    "agencyPrefix",
    "internationalPrefix",
    "measures",
];
/** Every action the modules write to the audit log. */
export var AUDIT_ENTITY_ACTIONS = [
    "create",
    "update",
    "delete",
    "password-change",
    "relink",
    "undo",
];
var ACTION_MESSAGE_KEYS = {
    create: "create",
    update: "update",
    delete: "delete",
    "password-change": "passwordChange",
    relink: "relink",
    undo: "undo",
};
var KNOWN_ENTITY_NAMES = new Set(AUDIT_ENTITY_NAMES);
/**
 * Czech label of an audited entity type.
 *
 * @param entityName the entity name as stored in the audit log
 * @param formatMessage react-intl formatMessage
 * @returns the label, or the raw name for a type this list does not know
 */
export var getAuditEntityLabel = function (entityName, formatMessage) {
    return KNOWN_ENTITY_NAMES.has(entityName)
        ? formatMessage({ id: "ui-br-users.auditLogs.entity.".concat(entityName) })
        : entityName;
};
/**
 * Czech label of an audited action.
 *
 * @param entityAction the action as stored in the audit log
 * @param formatMessage react-intl formatMessage
 * @returns the label, or the raw action for an unknown one
 */
export var getAuditActionLabel = function (entityAction, formatMessage) {
    var key = ACTION_MESSAGE_KEYS[entityAction];
    return key
        ? formatMessage({ id: "ui-br-users.auditLogs.select.option.".concat(key) })
        : entityAction;
};
/**
 * Reads the audit log filters from the URL query, so the list and the filter form both start from what the URL
 * says (the form's "Aplikovat" must not drop a filter that is shown).
 *
 * @param search the URL query, e.g. "?entityId=…&userId=…"
 * @returns the filters; old URLs used "id" for the user and "Všechny" for all actions
 */
export var parseAuditLogFilters = function (search) {
    var params = new URLSearchParams(search);
    var entityAction = params.get("entityAction");
    return {
        entityName: params.get("entityName") || "",
        entityId: params.get("entityId"),
        entityAction: entityAction === "Všechny" ? "" : entityAction,
        userId: params.get("userId") || params.get("id") || "",
        createDateFrom: params.get("createDateFrom") || null,
        createTimeFrom: params.get("createTimeFrom") || null,
    };
};
var PRAGUE_TIME_ZONE = "Europe/Prague";
var pragueParts = function (instant) {
    var parts = new Intl.DateTimeFormat("en-US", {
        timeZone: PRAGUE_TIME_ZONE,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).formatToParts(instant);
    var part = function (type) { var _a; return Number((_a = parts.find(function (p) { return p.type === type; })) === null || _a === void 0 ? void 0 : _a.value); };
    return {
        year: part("year"),
        month: part("month"),
        day: part("day"),
        hour: part("hour"),
        minute: part("minute"),
    };
};
/** Prague offset from UTC in minutes at the given instant. */
var pragueOffsetMinutes = function (instant) {
    var p = pragueParts(instant);
    var wallClockAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    var instantMinutes = Math.floor(instant.getTime() / 60000) * 60000;
    return Math.round((wallClockAsUtc - instantMinutes) / 60000);
};
var pad = function (value) { return String(value).padStart(2, "0"); };
var formatOffset = function (minutes) {
    var sign = minutes < 0 ? "-" : "+";
    var abs = Math.abs(minutes);
    return "".concat(sign).concat(pad(Math.floor(abs / 60)), ":").concat(pad(abs % 60));
};
var todayInPrague = function () {
    var p = pragueParts(new Date());
    return "".concat(p.year, "-").concat(pad(p.month), "-").concat(pad(p.day));
};
/**
 * Builds the createTimeFrom filter value: the date and time the user entered, read as Prague local time, with
 * the Prague offset valid at that moment (+01:00 in winter, +02:00 in summer).
 *
 * @param date the date as YYYY-MM-DD; today in Prague when empty
 * @param time the time as HH:MM; midnight when empty
 * @returns an ISO-8601 timestamp with offset, e.g. "2026-07-15T08:30:00+02:00"
 */
export var buildCreateTimeFrom = function (date, time) {
    var day = date || todayInPrague();
    var clock = !time || time === "null" ? "00:00" : time;
    var _a = day.split("-").map(Number), year = _a[0], month = _a[1], dayOfMonth = _a[2];
    var _b = clock.split(":").map(Number), hour = _b[0], minute = _b[1];
    var wallClockAsUtc = Date.UTC(year, month - 1, dayOfMonth, hour, minute);
    // the offset at the guessed instant, re-checked once so a time near the DST switch gets the right one
    var offset = pragueOffsetMinutes(new Date(wallClockAsUtc));
    offset = pragueOffsetMinutes(new Date(wallClockAsUtc - offset * 60000));
    return "".concat(day, "T").concat(pad(hour), ":").concat(pad(minute), ":00").concat(formatOffset(offset));
};
/** Days the backend limits an audit log query to when nothing selective is given (DB-876). */
export var AUDIT_DEFAULT_WINDOW_DAYS = 30;
/**
 * Whether the backend limits the list to the last {@link AUDIT_DEFAULT_WINDOW_DAYS} days: it does so when the query
 * names no date/time, no entity id and no user (DB-876), so the list can say so.
 *
 * @param filters the applied filters
 * @returns true when only the recent records are listed
 */
export var isDefaultWindowApplied = function (filters) {
    return !filters.createDateFrom &&
        !filters.createTimeFrom &&
        !filters.entityId &&
        !filters.userId;
};
/**
 * Text of the record count. The backend counts at most 10 000 records past the page start (DB-876); beyond that the
 * total is a lower bound (totalRecordsExact false) and is shown as "more than".
 *
 * @param totalRecords the total from the backend
 * @param exact totalRecordsExact from the backend (missing = exact, older backends)
 * @param formatMessage react-intl formatMessage
 * @returns the count text
 */
export var formatAuditRecordCount = function (totalRecords, exact, formatMessage) {
    return exact === false
        ? formatMessage({ id: "ui-br-users.auditLogs.recordsMoreThan" }, { count: totalRecords - 1 })
        : formatMessage({ id: "ui-br-users.table.subtitle.records-found" }, { count: totalRecords });
};
